import { useCallback, useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import axios from "axios";
import { API } from "@/lib/api";
import SideDrawer from "@/components/SideDrawer";
import Hero from "@/components/Hero";
import Manifesto from "@/components/Manifesto";
import BrandMarquee from "@/components/BrandMarquee";
import GallerySection from "@/components/GallerySection";
import SportigoSection from "@/components/SportigoSection";
import LogbookSection from "@/components/LogbookSection";
import InstagramSection from "@/components/InstagramSection";
import Footer from "@/components/Footer";
import { ContentContext } from "@/lib/content";

export default function Landing() {
  const [site, setSite] = useState(null);
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/site`)
      .then((res) => setSite(res.data))
      .catch(() => setSite({ hero_url: null, galleries: {} }));
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem("fs_tracked")) return;
    sessionStorage.setItem("fs_tracked", "1");
    axios
      .post(`${API}/track`, { path: window.location.pathname, referrer: document.referrer || "" })
      .catch(() => {});
  }, []);

  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (lenisRef.current) {
      lenisRef.current.scrollTo(el, { duration: 1.5 });
    } else {
      el.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const galleries = site?.galleries || {};

  return (
    <ContentContext.Provider value={site?.content || {}}>
      <main data-testid="landing-page" className="relative bg-ink text-bone">
      <SideDrawer onNavigate={scrollTo} />
      <Hero heroUrl={site?.hero_url} onScrollDown={() => scrollTo("la-palestra")} />
      <Manifesto />
      <BrandMarquee />

      <GallerySection
        id="sala-pesi"
        index="01"
        title="Sala Pesi"
        subtitle="Il cuore della Full Stack: macchinari dei migliori marchi al mondo, spazi studiati per allenarti senza compromessi."
        images={galleries.sala_pesi}
      />
      <GallerySection
        id="sala-posing"
        index="02"
        title="Sala Posing"
        subtitle="Uno spazio dedicato per controllare i propri miglioramenti fisici, con le luci adatte per valorizzare ogni minimo dettaglio."
        images={galleries.sala_posing}
      />
      <GallerySection
        id="zona-relax"
        index="03"
        title="Zona Relax"
        subtitle="Il recupero è parte dell'allenamento: un ambiente curato e accogliente per ricaricare le energie."
        images={galleries.zona_relax}
      />

      <SportigoSection
        id="abbonamenti"
        index="04"
        title="Abbonamenti e Ingressi"
        description="Da qui puoi iscriverti alla Full Stack e acquistare direttamente il tuo abbonamento o il tuo ingresso: scegli la formula che fa per te, completa l'acquisto online in pochi click e vieni ad allenarti. Accesso illimitato 24 ore su 24, 365 giorni all'anno."
        component="BuyFromList"
        containerId="sportigo-buy-from-list-container"
        options={{ inline: false, hideFilters: false, initOfferPayment: { cancelable: false } }}
      />
      <SportigoSection
        id="coaching"
        index="05"
        title="Coaching One to One"
        description="Vuoi un percorso su misura? Da qui puoi prenotare direttamente le tue lezioni personal one to one con i nostri coach: scegli giorno e orario, e lavora con un professionista su tecnica, programmazione e risultati."
        component="AppointmentFull"
        containerId="sportigo-appointment-full-container"
        options={{}}
      />
      <SportigoSection
        id="gift-card"
        index="06"
        title="Gift Card"
        description="Fai il regalo più utile: una Gift Card Full Stack. Da qui puoi acquistarla in pochi click e regalarla a chi vuoi — è utilizzabile su qualsiasi abbonamento e su tutti gli articoli in sede."
        component="GiftCard"
        containerId="sportigo-giftcard-container"
        options={{}}
      />

      <LogbookSection />

      <GallerySection
        id="brand"
        index="08"
        title="I Nostri Brand"
        subtitle="I prodotti che potrai acquistare in sede per poterti allenare al meglio!"
        images={galleries.brand}
      />

      <InstagramSection />

      <Footer />
      </main>
    </ContentContext.Provider>
  );
}
